import {
  View,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Text,
  Linking,
} from 'react-native';
import React, {useState, useEffect} from 'react';
import NavigationBar from 'NavigationBar';
import {_m} from 'INTL';
import WithdrawalManager from '../WithdrawalManager';
import {BankAccount, StoreInfoObject, StoreOfferInfo} from 'StoreInfo';
import LinkBankButton from './LinkBankButton';
import AccountDetails from './AccountDetails/AccountDetails';
import WithdrawalOption from './WithdrawalOption';
import CurrentUserStore from 'CurrentUserStore';
import BankAccountSelection from './BankAccountSelection/BankAccountSelection';
import WithdrawButton from './WithdrawButton';
import ModalNavigationController from 'ModalNavigationController';
import LoadingIndicator from 'LoadingIndicator';
import WithdrawalInput from './WithdrawalInput';
import NavigationManager from 'NavigationManager';
import SKZToaster from 'SKZToaster';
import WithdrawalController from 'WithdrawalController';
import SKZConstants from 'SKZConstants';
import WithdrawalErrorModal from './Modals/WithdrawalErrorModal';
import {
  AEROPAY_FAQ_URL,
  BANK_WITHDRAWAL_ERROR_MODAL_DEFAULTS,
  EBankWithdrawalOptions,
} from './BankWithdrawalConstants';
import {QUERY_KEYS, usePaymentDataQuery} from 'BankWithdrawalHooks';
import AeropayController from 'AeropayController';
import {useQueryClient} from '@tanstack/react-query';
import WithdrawalInfoList from './AccountDetails/WithdrawalInfoList';
import {BankWithdrawalConfirmationModal} from 'BankWithdrawalConfirmationModal';
import FooterSelectionDrawer from './BankAccountSelection/FooterSelectionDrawer';
import {
  WITHDRAWAL_CONFIRMATION_MODAL_CLICK,
  WITHDRAWAL_ADD_NEW_BANK_CLICK,
  WITHDRAWAL_BANK_NAVIGATOR_OPTION_CLICK,
} from 'AWSConstants';
import SkillzImage from 'SkillzImage';
import {WithdrawalOptionScreen} from '../WithdrawalOptionsScreen';
import SkillzStyle from 'SkillzStyle';
import GlobalEventEmitter from 'GlobalEventEmitter';
import CancelWithdrawal from 'CancelWithdrawal';
import ClientPropertyConstants from 'ClientPropertyConstants';
import DataDogRUM from 'DataDogRUM';
import SkillzSVG from 'SkillzSVG';
import {Badge} from 'Badge';
import SecureBankingDrawer from 'SecureBankingDrawer';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';

const {textColorPrimary, fontRegular} = SkillzStyle.fullStyles;

type BankWithdrawalScreenProps = {
  hasBackButton?: boolean;
  onBackNavigation?: () => void;
  onWithdrawalSuccess?: (
    withdrawalAmount: string,
    canPerformRTPWithdrawal: boolean,
    rtpEnabled: boolean
  ) => void;
  withdrawOption: EBankWithdrawalOptions;
  launchBankLinkOnMount?: boolean;
  rtpEnabled?: boolean;
};

type AccountDetails = {
  cash_balance: number;
  bonus_balance: number;
  real_cash_balance: number;
  withdrawable_balance: number;
};

const BankWithdrawalScreen = ({
  hasBackButton,
  onBackNavigation,
  onWithdrawalSuccess,
  withdrawOption,
  launchBankLinkOnMount,
  rtpEnabled,
}: BankWithdrawalScreenProps) => {
  const [isSecurityPopupVisible, setIsSecurityPopupVisible] = useState(false);

  const securityPopUp = () => {
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_BANK_PAGE_CTA_CLICK,
      payload: {
        cta: 'Security Info',
      },
      includeUserWalletDetails: true,
    });
    setIsSecurityPopupVisible(true);
  };
  const handleSecurityDismiss = () => setIsSecurityPopupVisible(false);

  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [isFooterDrawerVisible, setIsFooterDrawerVisible] = useState(false);

  const playthroughEnabled = ClientPropertyConstants.getPlaythroughEnabled();

  const queryClient = useQueryClient();
  const {
    data: paymentData,
    isInitialLoading: isPaymentDataInitialLoading,
    isFetchedAfterMount: isPaymentDataFetchedAfterMount,
    isLoading: isPaymentDataLoading,
    isRefetching: isPaymentDataRefetching,
    refetch: refetchPaymentData,
  } = usePaymentDataQuery({
    refetchOnMount: true,
  });

  const bankAccountList = paymentData?.bank_accounts || [];
  const hasLinkedBanks = bankAccountList.length > 0;
  const rtpEligible = paymentData?.aeropay?.rtp?.eligible || false;
  const rtpData = paymentData?.aeropay?.rtp || null;

  // Only allow users in the RTP segment under the limits to perform RTP withdrawals
  // Limits are provided by the payment-data endpoint
  const isWithinRTPTransactionLimits =
    rtpData?.daily_frequency_count !== undefined &&
    rtpData?.daily_frequency_count_limit !== undefined &&
    rtpData?.daily_frequency_count < rtpData?.daily_frequency_count_limit;

  const isWithinRTPAmountLimit =
    rtpData?.withdrawal_amount_limit &&
    (Number(withdrawalAmount) || 0) <= rtpData?.withdrawal_amount_limit;

  const canPerformRTPWithdrawal =
    rtpEnabled && rtpEligible && isWithinRTPTransactionLimits && isWithinRTPAmountLimit;

  // TODO: move this to a separate tanstack query hook
  const fetchUserInfo = async () => {
    await CurrentUserStore.fetchUser(true)
      .catch((error: Object) => {
        console.warn('BankWithdrawalScreen: Failed to fetch user info:', error);
      })
      .then((user: any) => {
        const details = {
          cash_balance: user?.cash_balance || 0,
          bonus_balance: user?.bonus_balance || 0,
          real_cash_balance: user?.real_cash_balance || 0,
          withdrawable_balance: user?.withdrawable_balance || 0,
        };

        setAccountDetails(details);
      });
  };

  useEffect(() => {
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_BANK_PAGE_LOAD,
      payload: {
        instant_available: rtpEnabled,
      },
      includeUserWalletDetails: true,
    });
    fetchUserInfo();

    const screenFocusSubscription = GlobalEventEmitter.addListener(
      'BankWithdrawalScreenWillAppear',
      () => {
        /*
         * This is used to refresh the bank accounts and user info when the
         * BankWithdrawalScreen is already on route stack and get focused
         * from another screen (Deposit or Home).
         * This is important because the bank accounts and user info may have changed.
         */
        refetchPaymentData();
      }
    );

    return () => {
      screenFocusSubscription.remove();
    };
  }, []);

  const handleInitialBankAccountSelection = () => {
    if (!bankAccountList.length) {
      return;
    }
    const preferredBankAccount = bankAccountList.find((bank) => bank.is_selected);
    if (preferredBankAccount) {
      setSelectedBankAccount(preferredBankAccount);
    } else {
      setSelectedBankAccount(bankAccountList[0] as BankAccount);
    }
  };

  // Set the selected bank account when the component mounts or when the bank account list changes
  // This ensures that the selected bank account is set correctly when the component is first rendered
  useEffect(() => {
    handleInitialBankAccountSelection();
  }, [bankAccountList]);

  // Handles the 'Link Bank' button press from the bank withdrawal popup
  useEffect(() => {
    isPaymentDataFetchedAfterMount && launchBankLinkOnMount && handleBankLinkBtnPress();
  }, [isPaymentDataFetchedAfterMount, launchBankLinkOnMount]);

  // Select the preferred bank account when the bank list is updated
  useEffect(() => {
    const preferredBankAccount = bankAccountList.find((bank) => bank.is_selected);
    if (preferredBankAccount) {
      setSelectedBankAccount(preferredBankAccount);
    }
  }, [bankAccountList]);

  const handleBankLinkBtnPress = async () => {
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_BANK_PAGE_CTA_CLICK,
      payload: {
        cta: 'Link Bank',
      },
      includeUserWalletDetails: true,
    });
    WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_ADD_NEW_BANK_CLICK, {});
    setIsFooterDrawerVisible(false);
    if (!paymentData) {
      return;
    }

    const storeInfo = new StoreInfoObject({} as StoreOfferInfo, paymentData);
    const bankLinkResult = await AeropayController.launchACHBankLinkFlow(storeInfo, 'WITHDRAWAL');

    // User dismissed the bank link flow
    if (bankLinkResult.action === 'ACH_DEPOSIT_DISMISSED') {
      return;
    }

    // User successfully finished the MFA process
    if (bankLinkResult.action === 'MFA_COMPLETED') {
      // refresh the bank accounts
      queryClient.invalidateQueries([QUERY_KEYS.paymentData]);

      // hide loading and pop MFA screen
      LoadingIndicator.hide();
      NavigationManager.pop();
      return;
    }

    // User successfully linked a bank account
    if (bankLinkResult.action === 'CREATED_BANK_ACCOUNT') {
      // refresh the bank accounts
      queryClient.invalidateQueries([QUERY_KEYS.paymentData], undefined);

      // hide loading and pop widget screen
      LoadingIndicator.hide();
      NavigationManager.pop();

      SKZToaster.show('success', _m('Bank account linked successfully!'));
      return;
    }
  };

  const showConfirmationModal = () => {
    const confirmationModal = (
      <BankWithdrawalConfirmationModal
        withdrawalAmount={+withdrawalAmount}
        bonusBalance={accountDetails?.bonus_balance || 0}
        onConfirm={handleConfirmDepositDetails}
        onDismiss={() => {
          trackTelemetryEvent({
            event: TelemetryEventName.WITHDRAW_CONFIRM_POPUP_CTA_CLICK,
            payload: {
              cta: 'cancel',
            },
            includeUserWalletDetails: true,
          });
          WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_CONFIRMATION_MODAL_CLICK, {
            WITHDRAWAL_METHOD: 'BANK',
            ACTION: 'CANCEL',
          });
          ModalNavigationController.hideModal();
        }}
        isRTPEnabled={Boolean(rtpEnabled && canPerformRTPWithdrawal)}
      />
    );
    ModalNavigationController.showModal(confirmationModal);
  };
  const handleWithdrawButtonPress = () => {
    // TODO: Add fee_charged property for the telemetry event in case of fast/instant withdrwal
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_START_WITHDRAW_CLICK,
      payload: {
        withdraw_method: canPerformRTPWithdrawal ? 'Instant' : 'Fast',
        withdraw_amount: withdrawalAmount,
        bc_forfeit: accountDetails?.bonus_balance,
      },
      includeUserWalletDetails: true,
    });
    showConfirmationModal();
  };

  const handleBankAccountSelect = (account: BankAccount) => {
    setSelectedBankAccount(account);
    setIsFooterDrawerVisible(false);
  };

  const handlePerformTransaction = () => {
    LoadingIndicator.show();
    // TODO: Change this to use Tanstack Mutation
    const withdrawalMethod = canPerformRTPWithdrawal
      ? SKZConstants.AEROPAY_RTP_WITHDRAWAL
      : SKZConstants.AEROPAY_WITHDRAWAL;

    WithdrawalController.withdraw(
      withdrawalMethod,
      Number(withdrawalAmount),
      '',
      selectedBankAccount?.id
    )
      .then((response: any) => {
        LoadingIndicator.hide();
        onWithdrawalSuccess?.(
          withdrawalAmount,
          Boolean(canPerformRTPWithdrawal),
          Boolean(rtpEnabled)
        );
      })
      .catch((error: any) => {
        LoadingIndicator.hide();
        showBankWithdrawalErrorModal();
      })
      .finally(() => {
        // dismisses the phone verification modal
        NavigationManager.pop();
        queryClient.invalidateQueries([QUERY_KEYS.paymentData]);
      });
  };

  const showBankWithdrawalErrorModal = () => {
    const bankWithdrawalErrorModal = (
      <WithdrawalErrorModal
        bodyText={BANK_WITHDRAWAL_ERROR_MODAL_DEFAULTS.bodyText}
        onDismiss={() => {
          ModalNavigationController.hideModal();
        }}
      />
    );
    ModalNavigationController.showModal(bankWithdrawalErrorModal);
  };

  const handleConfirmDepositDetails = () => {
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_CONFIRM_POPUP_CTA_CLICK,
      payload: {
        cta: 'confirm',
      },
      includeUserWalletDetails: true,
    });
    WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_CONFIRMATION_MODAL_CLICK, {
      WITHDRAWAL_METHOD: 'BANK',
      ACTION: 'CONFIRM',
    });
    ModalNavigationController.hideModal();
    WithdrawalManager.showSMSVerification((success: boolean) => {
      if (success) {
        const toastDuration = 1500;
        SKZToaster.show('success', _m('Account Verified!'), toastDuration);

        setTimeout(() => {
          handlePerformTransaction();
        }, toastDuration);
      } else {
        NavigationManager.pop();
      }
    });
  };

  const handleNavigationToWithdrawalMethodsScreen = () => {
    trackTelemetryEvent({
      event: TelemetryEventName.WITHDRAW_BANK_PAGE_CTA_CLICK,
      payload: {
        cta: 'Change Payment Method,',
      },
      includeUserWalletDetails: true,
    });
    WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_BANK_NAVIGATOR_OPTION_CLICK, {});
    NavigationManager.isSceneInRoute('WithdrawalOptionScreen')
      ? NavigationManager.pop()
      : NavigationManager.pushComponentWithScene(
          <WithdrawalOptionScreen
            hasBackButton={true}
            onBackNavigation={(): void => NavigationManager.pop()}
          />,
          true,
          'WithdrawalOptionScreen'
        );
  };

  if (isPaymentDataInitialLoading || isPaymentDataLoading || isPaymentDataRefetching) {
    return <LoadingIndicator isInline />;
  }

  return (
    <SafeAreaView style={styles.container}>
      <NavigationBar
        hasBackButton={hasBackButton}
        onBackButtonPress={onBackNavigation}
        title={_m('WITHDRAW')}
        hasRightComponent
        renderRightComponent={
          <TouchableOpacity onPress={handleNavigationToWithdrawalMethodsScreen} hitSlop={10}>
            <SkillzImage
              style={styles.changeWithdrawalMethodIcon}
              source='ic_edit_button_rounded.svg'
              tintColor={textColorPrimary.color}
            />
          </TouchableOpacity>
        }
      />
      <ScrollView>
        <View style={styles.withdrawalDetailsContainer}>
          {rtpEnabled && (
            <>
              <TouchableOpacity
                onPress={securityPopUp}
                hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
                style={{position: 'absolute', top: 10, right: 10, zIndex: 100}}
              >
                <SkillzImage
                  style={styles.shieldIcon}
                  source='icon_shield_checkmark.svg'
                  tintColor='FFFFFF'
                />
              </TouchableOpacity>
            </>
          )}
          <WithdrawalOption selectedOption={withdrawOption} />
          {!hasLinkedBanks && <LinkBankButton onPress={handleBankLinkBtnPress} />}
          {hasLinkedBanks && (
            <BankAccountSelection
              bankAccounts={bankAccountList}
              selectedBankAccount={selectedBankAccount}
              onSelect={() => setIsFooterDrawerVisible(true)}
            />
          )}
          {!hasLinkedBanks && (
            <TouchableOpacity
              onPress={() => {
                Linking.openURL(AEROPAY_FAQ_URL);
                trackTelemetryEvent({
                  event: TelemetryEventName.WITHDRAW_BANK_PAGE_CTA_CLICK,
                  payload: {
                    cta: 'See FAQ',
                  },
                  includeUserWalletDetails: true,
                });
              }}
            >
              <Text style={styles.faqLink}>{_m('See FAQ for more info')}</Text>
            </TouchableOpacity>
          )}
          {accountDetails && (
            <WithdrawalInput
              withdrawLimit={
                playthroughEnabled
                  ? accountDetails.withdrawable_balance
                  : accountDetails.real_cash_balance
              }
              onChangeAmount={setWithdrawalAmount}
              rtpEnabled={rtpEnabled}
              rtpEligible={rtpEligible}
              rtpData={rtpData}
            />
          )}
        </View>
        {accountDetails && <AccountDetails accountDetails={accountDetails} />}
        {ClientPropertyConstants.getPlayerWithdrawalCancellationAllEnabled() && (
          <CancelWithdrawal
            containerStyleOverride={{
              ...styles.cancelWithdrawalContent,
              ...styles.cancelWithdrawalContainer,
            }}
          />
        )}
        <WithdrawalInfoList isRTPEnabled={rtpEnabled} />
        <SkillzSVG style={styles.aeropayIcon} source='secured_by_aeropay.svg' />
      </ScrollView>

      <WithdrawButton
        onPress={handleWithdrawButtonPress}
        disabled={!selectedBankAccount || !withdrawalAmount}
      />
      <FooterSelectionDrawer
        bankAccounts={bankAccountList}
        onSelect={handleBankAccountSelect}
        onLinkNewBankAccount={handleBankLinkBtnPress}
        isDrawerVisible={isFooterDrawerVisible}
        onDismiss={() => setIsFooterDrawerVisible(false)}
        selectedBankAccount={selectedBankAccount}
      />
      <SecureBankingDrawer visible={isSecurityPopupVisible} onDismiss={handleSecurityDismiss} />
    </SafeAreaView>
  );
};

export default BankWithdrawalScreen;

// TODO: Update styles according to designs and move to separate file
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAFAFA',
    marginHorizontal: 0,
  },
  withdrawalDetailsContainer: {
    marginTop: 10,
    marginHorizontal: 10,
    borderRadius: 5,
    borderColor: '#DCDCDC',
    borderWidth: 1,
    backgroundColor: '#FFF',
    paddingTop: 16,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  cancelWithdrawalContainer: {
    borderColor: '#DCDCDC',
    borderWidth: 1,
    marginTop: 10,
    borderRadius: 5,
    marginHorizontal: 10,
    height: 96,
    overflow: 'hidden',
  },
  cancelWithdrawalContent: {
    flex: 1,
    marginTop: 0,
  },
  changeWithdrawalMethodIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  faqLink: {
    fontSize: 13,
    flex: 1,
    textDecorationLine: 'underline',
    letterSpacing: -0.1,
    lineHeight: 16,
    color: '#367BCE',
    alignSelf: 'center',
    marginTop: 15,
    ...fontRegular,
  },
  aeropayIcon: {
    width: 120,
    height: 32,
    alignSelf: 'center',
    marginVertical: 0,
  },
  shieldIcon: {
    width: 17,
    height: 19,
    flexShrink: 0,
    position: 'absolute',
    top: 0,
    right: 1,
    zIndex: 100,
  },
});
